import React from "react";
import { motion } from "framer-motion";
import {
    Briefcase,
    TrendingUp,
    Users,
    Globe,
    HeartPulse,
    BookOpen,
    ArrowRight,
    Eye,
} from "lucide-react";
import { Link } from "react-router-dom";

// Imported Assets
import researchPaper from "../../../docs/Executive Summary KU Research.pdf";
import whatWeDoImage from "../../../images/whatisku.png";
import studyImage from "../../../images/study.png";

// --- SECTION 1: Why Volunteer ---

const benefits = [
    { icon: Globe, title: "Social Awareness", desc: "Engage with diverse communities to build empathy and understand different perspectives - vital traits in a globalized world.", color: "bg-pink-500" },
    { icon: HeartPulse, title: "Mental & Physical Health", desc: "Reduce anxiety and loneliness by building strong social connections that contribute to overall flourishing and life satisfaction.", color: "bg-emerald-500" },
    { icon: Users, title: "Networking & Connection", desc: "Connect with like-minded peers and industry professionals, providing valuable networking opportunities for your future.", color: "bg-orange-500" },
    { icon: TrendingUp, title: "Leadership Skills", desc: "Gain practical experience in teamwork, communication, leadership, event coordination, stakeholder engagement, and problem-solving skills highly valued by employers across sectors.", color: "bg-purple-600" },
    { icon: Briefcase, title: "Career & Employability", desc: "Build a professional portfolio and stand out to employers by demonstrating initiative and a commitment to social responsibility.", color: "bg-blue-500" }
];

const BenefitsSection = () => (
    <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">

            {/* Left Side: Image */}
            <div className="lg:col-span-5">
                <img src={whatWeDoImage} alt="What we do" className="w-full h-auto rounded-3xl" />
            </div>

            {/* Right Side: Why Volunteer & Benefits */}
            <div className="lg:col-span-7 flex flex-col gap-10">

                {/* Heading Section */}
                <div className="space-y-4">
                    <h2 className="font-display text-5xl font-bold text-[#4B2E83]">Why Volunteer with KU?</h2>
                    <p className="text-lg text-gray-700 leading-relaxed text-justify">
                        Volunteering with us is more than just giving back. It is a structured pathway to bridge the gap between your education and your future career while cultivating the compassionate leader within you.
                    </p>
                </div>

                {/* Benefits Grid */}
                <div className="grid grid-cols-1 gap-6">
                    {benefits.map((benefit, i) => (
                        <motion.div
                            key={benefit.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="flex items-start p-6 bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300"
                        >
                            {/* Icon container */}
                            <div className={`w-12 h-12 rounded-2xl ${benefit.color} flex items-center justify-center flex-shrink-0 mr-4`}>
                                <benefit.icon className="text-white w-6 h-6" />
                            </div>

                            {/* Text container */}
                            <div>
                                <h3 className="font-display font-bold text-xl text-[#4B2E83] leading-tight mb-1">
                                    {benefit.title}
                                </h3>
                                <p className="font-body text-gray-600 leading-snug text-justify">
                                    {benefit.desc}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                    <div className="flex justify-start mt-4">
                        <Link
                            to="/volunteering"
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-accent font-bold text-ku-purple-deep bg-ku-gold shadow-gold-elevated hover:shadow-gold-glow hover:scale-[1.03] active:scale-[0.97] transition-all duration-300 text-base"
                        >
                            Volunteer Now
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                    </div>
                </div>

            </div>
        </div>
    </section>
);

const ResearchSection = () => (
    <section className="py-16 bg-gray-50 relative overflow-hidden border-t border-gray-200">
        <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

            {/* Header section */}
            <div className="text-center max-w-5xl mx-auto mb-12 px-4">
                <span className="inline-flex items-center justify-center gap-2 px-5 py-1.5 rounded-full border border-gray-200 text-[#4B2E83] bg-white font-accent text-[11px] font-bold uppercase mb-4 tracking-widest shadow-sm">
                    <BookOpen className="w-3 h-3 text-[#FDBA21]" />
                    Research & Insights
                </span>
                
                <h2 className="font-display text-3xl md:text-4xl lg:text-[2.8rem] font-bold text-[#4B2E83] mb-6 leading-tight w-full">
                    Find Yourself Through Volunteering
                </h2>
                
                <p className="font-body text-lg md:text-xl text-gray-800 leading-relaxed font-bold max-w-3xl mx-auto">
                    Discover insights from a TISS-led year-long research study on Volunteering and its Impact on Mental Health in College Students.
                </p>
            </div>

            <div className="space-y-10">
                
                {/* Media Container - 3 Column Grid (Image, YouTube, Insta) */}
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-center w-full">
                        
                        {/* 1. Study Image - Left side (WITH HEADING) */}
                        <div className="relative rounded-[1.5rem] overflow-hidden shadow-lg bg-white w-full h-[400px] lg:h-[450px] p-6 flex flex-col items-center border border-gray-100">
                            <h3 className="font-display text-xl font-bold text-[#4B2E83] mb-4 text-center w-full">
                                Key Research Findings
                            </h3>
                            <div className="w-full flex-1 relative flex items-center justify-center">
                                <img 
                                    src={studyImage} 
                                    alt="Pre-Intervention Strong Positive Correlations" 
                                    className="absolute inset-0 w-full h-full object-contain"
                                />
                            </div>
                        </div>

                        {/* 2. YouTube Video Card - Middle */}
                        <div className="relative rounded-[1.5rem] overflow-hidden shadow-lg bg-black w-full h-[400px] lg:h-[450px]">
                            <iframe
                                src="https://www.youtube.com/embed/ZhQpA77N5-o"
                                title="YouTube video player"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                                className="absolute inset-0 w-full h-full border-0 object-cover"
                            ></iframe>
                        </div>

                        {/* 3. Instagram Reel Card - Right side */}
                        <div className="relative rounded-[1.5rem] overflow-hidden shadow-lg bg-white w-full h-[400px] lg:h-[450px] flex items-center justify-center">
                            <iframe 
                                src="https://www.instagram.com/reel/DQL6VUADMWu/embed" 
                                title="Instagram Reel"
                                frameBorder="0" 
                                scrolling="no" 
                                allowTransparency="true"
                                className="absolute inset-0 w-full h-full border-0"
                            ></iframe>
                        </div>
                        
                    </div>
                </motion.div>

                {/* NEW: Purple Box with Summary (Left) and Actions (Right) */}
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
                    <div className="bg-[#4B2E83] rounded-[1.5rem] p-6 md:p-8 text-white flex flex-col md:flex-row items-center justify-between gap-8 shadow-xl max-w-7xl mx-auto mt-8">
                        
                        {/* Left Side: Summary Text */}
                        <div className="flex-1 max-w-3xl">
                            <div className="flex items-center gap-3 mb-2">
                                {/* <BookOpen className="w-6 h-6 text-[#FDBA21]" /> */}
                                <h3 className="font-display text-xl font-bold text-white">Research Summary</h3>
                            </div>
                            <p className="font-body text-white/95 leading-relaxed text-[16px] md:text-left text-justify">
                                This collaborative research reveals that structured volunteering is a proven, proactive model for enhancing youth mental well-being, effectively bridging the gap between academic education and students' holistic growth.
                            </p>
                        </div>

                        {/* Right Side: Actions (View & Download) */}
                        <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row gap-4 flex-shrink-0 w-full md:w-auto">
                            <a
                                href={researchPaper}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#FDBA21] text-[#4B2E83] font-accent font-bold px-8 py-3.5 rounded-full hover:bg-white transition-all duration-300 shadow-md text-[15px] whitespace-nowrap"
                            >
                                <Eye className="w-5 h-5" />
                                <span>View Report</span>
                            </a>
                            {/* <a
                                href={researchPaper}
                                download="KU_Research_Executive_Summary.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-transparent border border-white text-white font-accent font-bold px-8 py-3.5 rounded-full hover:bg-white hover:text-[#4B2E83] transition-all duration-300 shadow-md text-[15px] whitespace-nowrap"
                            >
                                <Download className="w-5 h-5" />
                                <span>Download PDF</span>
                            </a> */}
                        </div>

                    </div>
                </motion.div>
            </div>
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
                <hr className="border-t border-gray-200" />
            </div>
        </div>
    </section>
);

// --- MAIN EXPORT ---
const Volunteer = () => {
    return (
        <div className="w-full">
            <BenefitsSection />
            <ResearchSection />
        </div>
    );
};

export default Volunteer;